import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, Save, X, Globe, Search, BarChart3, Zap, RefreshCw, ExternalLink, CheckCircle, AlertTriangle, XCircle, TrendingUp } from 'lucide-react';

const gold = '#C9A961';
const CATEGORIES = ["Numerology", "Astrology", "Vastu", "Remedies", "Tarot", "Palmistry", "Healing", "Spirituality", "Tantra", "Rare Systems", "Applied Occult", "Lifestyle", "General"];

export default function AdminBlogEditor({ adminToken }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('posts'); // posts | seo | trending | generate | logs
  const [seoData, setSeoData] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genKeyword, setGenKeyword] = useState('');
  const [genCategory, setGenCategory] = useState('General');
  const [genAutoPublish, setGenAutoPublish] = useState(true);
  const [indexStatus, setIndexStatus] = useState(null);
  const [indexLoading, setIndexLoading] = useState(false);
  const [rankingData, setRankingData] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/blog/admin/posts?limit=50', { headers });
      setPosts(res.data.posts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const newPost = () => setEditing({
    title: '', content_html: '', excerpt: '', meta_title: '', meta_description: '',
    featured_image: '', category: 'General', tags: [], author: 'Astrowins',
    status: 'draft', region_target: 'all', schema_faq: [],
  });

  const savePost = async () => {
    if (!editing.title.trim()) { setMessage('Title is required'); return; }
    setSaving(true);
    setMessage('');
    try {
      if (editing.id) {
        await axios.put(`/api/blog/admin/posts/${editing.id}`, editing, { headers });
        setMessage('Post updated!');
      } else {
        const res = await axios.post('/api/blog/admin/posts', editing, { headers });
        setMessage('Post created!');
        setEditing(prev => ({ ...prev, id: res.data.post?.id }));
      }
      fetchPosts();
    } catch (e) { setMessage(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await axios.delete(`/api/blog/admin/posts/${id}`, { headers });
      fetchPosts();
    } catch (e) { console.error(e); }
  };

  const publishPost = async (post) => {
    try {
      await axios.put(`/api/blog/admin/posts/${post.id}`, { status: 'published' }, { headers });
      fetchPosts();
    } catch (e) { console.error(e); }
  };


  const fetchSEO = async () => {
    setSeoLoading(true);
    try {
      const res = await axios.get('/api/blog/admin/seo-dashboard', { headers });
      setSeoData(res.data);
    } catch (e) { console.error(e); }
    finally { setSeoLoading(false); }
  };

  const discoverTrends = async () => {
    setGenLoading(true);
    try {
      const res = await axios.post('/api/blog/admin/discover-trends', {}, { headers });
      setMessage(`Discovered ${res.data.keywords?.length || 0} trending keywords`);
      fetchSEO();
    } catch (e) { setMessage('Trend discovery failed'); }
    finally { setGenLoading(false); }
  };

  const generatePosts = async (keyword) => {
    setGenLoading(true);
    try {
      const body = keyword ? { keyword, category: genCategory, auto_publish: genAutoPublish } : { auto_publish: genAutoPublish };
      const res = await axios.post('/api/blog/admin/generate', body, { headers });
      setMessage(res.data.message || 'Generated!');
      fetchPosts();
      fetchSEO();
    } catch (e) { setMessage('Generation failed'); }
    finally { setGenLoading(false); }
  };

  const pingGoogle = async () => {
    try {
      const res = await axios.post('/api/blog/admin/ping-search-engines', {}, { headers });
      setMessage(`Pinged: ${res.data.results?.map(r => `${r.engine}:${r.status}`).join(', ')}`);
    } catch (e) { setMessage('Ping failed'); }
  };

  const buildLinks = async () => {
    try {
      const res = await axios.post('/api/blog/admin/build-internal-links', {}, { headers });
      setMessage(res.data.message);
    } catch (e) { setMessage('Link building failed'); }
  };

  const bulkPublish = async () => {
    try {
      const res = await axios.post('/api/blog/admin/bulk-publish', {}, { headers });
      setMessage(res.data.message);
      fetchPosts();
    } catch (e) { setMessage('Bulk publish failed'); }
  };

  useEffect(() => { if (activeTab === 'seo' || activeTab === 'trending' || activeTab === 'logs') fetchSEO(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'indexing') fetchIndexStatus(); }, [activeTab]);

  const fetchIndexStatus = async () => {
    setIndexLoading(true);
    try {
      const res = await axios.get('/api/blog/admin/index-status', { headers });
      setIndexStatus(res.data);
    } catch (e) { console.error(e); }
    finally { setIndexLoading(false); }
  };

  const indexSinglePost = async (slug) => {
    try {
      const res = await axios.post('/api/blog/admin/index-url', { slug }, { headers });
      setMessage(res.data.status === 'success' ? `Submitted ${slug} to Google!` : `Index failed: ${res.data.detail?.slice(0, 80)}`);
      fetchIndexStatus();
    } catch (e) { setMessage('Index submission failed'); }
  };

  const indexAllPosts = async () => {
    setIndexLoading(true);
    setMessage('Submitting all URLs to Google...');
    try {
      const res = await axios.post('/api/blog/admin/index-all', {}, { headers });
      setMessage(res.data.message);
      fetchIndexStatus();
    } catch (e) { setMessage('Bulk indexing failed'); }
    finally { setIndexLoading(false); }
  };

  const StatusIcon = ({ s }) => s === 'pass' ? <CheckCircle size={14} color="#10B981" /> : s === 'warn' ? <AlertTriangle size={14} color="#F59E0B" /> : <XCircle size={14} color="#EF4444" />;
  const scoreColor = (s) => s >= 80 ? '#10B981' : s >= 60 ? '#F59E0B' : s >= 40 ? '#FB923C' : '#EF4444';

  const fixAllSEO = async () => {
    setFixingAll(true);
    setMessage('Fixing SEO issues across all posts...');
    try {
      const res = await axios.post('/api/blog/admin/fix-seo', {}, { headers });
      const fixed = res.data.results?.length || 0;
      setMessage(`Fixed SEO on ${fixed} posts! Refresh to see updated scores.`);
      fetchSEO();
    } catch (e) { setMessage('SEO fix failed: ' + (e.response?.data?.detail || '')); }
    finally { setFixingAll(false); }
  };

  const fixSinglePost = async (postId) => {
    try {
      const res = await axios.post('/api/blog/admin/fix-seo', { post_id: postId }, { headers });
      setMessage(`Fixed: ${res.data.result?.changes?.join(', ') || 'No changes needed'}`);
      fetchSEO();
    } catch (e) { setMessage('Fix failed'); }
  };

  const fetchRanking = async () => {
    try {
      const res = await axios.get('/api/blog/admin/ranking?days=28', { headers });
      setRankingData(res.data);
    } catch (e) { setRankingData({ error: e.response?.data?.detail || 'Failed to fetch ranking data' }); }
  };

  useEffect(() => { if (activeTab === 'ranking') fetchRanking(); }, [activeTab]);

  const inputStyle = { background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#E5E7EB', padding: '0.6rem 0.85rem', width: '100%', fontSize: '0.9rem' };

  // EDIT VIEW
  if (editing) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', color: '#FFF' }}>{editing.id ? 'Edit Post' : 'New Blog Post'}</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={savePost} disabled={saving} style={{ background: gold, color: '#0A0F2A', padding: '0.5rem 1.5rem', borderRadius: '8px', fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save'}
            </button>
            {editing.status === 'draft' && editing.id && (
              <button onClick={() => { setEditing(p => ({ ...p, status: 'published' })); setTimeout(savePost, 100); }}
                style={{ background: '#10B981', color: '#FFF', padding: '0.5rem 1.2rem', borderRadius: '8px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                Publish
              </button>
            )}
            <button onClick={() => setEditing(null)} style={{ background: 'rgba(255,255,255,0.08)', color: '#9CA3AF', padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {message && <p style={{ color: message.includes('!') ? '#10B981' : '#EF4444', marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</p>}

        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Title *</label>
            <input style={inputStyle} value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))} placeholder="Blog post title" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Category</label>
              <select style={inputStyle} value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Status</label>
              <select style={inputStyle} value={editing.status} onChange={e => setEditing(p => ({ ...p, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Featured Image URL</label>
            <input style={inputStyle} value={editing.featured_image} onChange={e => setEditing(p => ({ ...p, featured_image: e.target.value }))} placeholder="https://..." />
          </div>

          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Excerpt (short summary for cards)</label>
            <textarea style={{ ...inputStyle, minHeight: '60px' }} value={editing.excerpt} onChange={e => setEditing(p => ({ ...p, excerpt: e.target.value }))} placeholder="Brief summary..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>SEO Title</label>
              <input style={inputStyle} value={editing.meta_title} onChange={e => setEditing(p => ({ ...p, meta_title: e.target.value }))} placeholder="SEO title (60 chars)" />
            </div>
            <div>
              <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>SEO Description</label>
              <input style={inputStyle} value={editing.meta_description} onChange={e => setEditing(p => ({ ...p, meta_description: e.target.value }))} placeholder="SEO description (160 chars)" />
            </div>
          </div>

          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Tags (comma-separated)</label>
            <input style={inputStyle} value={(editing.tags || []).join(', ')} onChange={e => setEditing(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))} placeholder="numerology, astrology, tips" />
          </div>

          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Region Target</label>
            <select style={inputStyle} value={editing.region_target} onChange={e => setEditing(p => ({ ...p, region_target: e.target.value }))}>
              <option value="all">All Regions</option>
              <option value="india">India</option>
              <option value="usa">USA</option>
              <option value="europe">Europe</option>
            </select>
          </div>

          <div>
            <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Content (HTML)</label>
            <textarea
              style={{ ...inputStyle, minHeight: '400px', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.6 }}
              value={editing.content_html}
              onChange={e => setEditing(p => ({ ...p, content_html: e.target.value }))}
              placeholder="<h2>Your heading</h2><p>Your content...</p>"
            />
          </div>

          {/* FAQ Section for Schema */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>FAQ Schema (for Google rich snippets)</label>
              <button onClick={() => setEditing(p => ({ ...p, schema_faq: [...(p.schema_faq || []), { question: '', answer: '' }] }))}
                style={{ background: 'rgba(201,169,97,0.15)', color: gold, border: `1px solid ${gold}`, borderRadius: '6px', padding: '0.3rem 0.8rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                + Add FAQ
              </button>
            </div>
            {(editing.schema_faq || []).map((faq, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input style={inputStyle} value={faq.question} onChange={e => {
                  const faqs = [...editing.schema_faq]; faqs[i] = { ...faqs[i], question: e.target.value };
                  setEditing(p => ({ ...p, schema_faq: faqs }));
                }} placeholder="Question" />
                <input style={inputStyle} value={faq.answer} onChange={e => {
                  const faqs = [...editing.schema_faq]; faqs[i] = { ...faqs[i], answer: e.target.value };
                  setEditing(p => ({ ...p, schema_faq: faqs }));
                }} placeholder="Answer" />
                <button onClick={() => setEditing(p => ({ ...p, schema_faq: p.schema_faq.filter((_, j) => j !== i) }))}
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: 'none', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // MAIN VIEW (Tabs: Posts | SEO Dashboard | Trending | Generate | Logs)
  return (
    <div>
      {message && <p style={{ color: message.includes('fail') ? '#EF4444' : '#10B981', marginBottom: '0.75rem', fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: 'rgba(16,185,129,0.08)', borderRadius: '6px' }}>{message}</p>}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[['posts', 'Posts', Plus], ['seo', 'SEO Audit', BarChart3], ['ranking', 'Live Ranking', TrendingUp], ['indexing', 'Google Index', Globe], ['trending', 'Trending Keywords', TrendingUp], ['generate', 'Generate', Zap], ['logs', 'Pipeline Logs', RefreshCw]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: activeTab === id ? 700 : 400, border: activeTab === id ? `2px solid ${gold}` : '1px solid rgba(255,255,255,0.1)', background: activeTab === id ? 'rgba(201,169,97,0.12)' : 'transparent', color: activeTab === id ? gold : '#9CA3AF', cursor: 'pointer' }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ============ POSTS TAB ============ */}
      {activeTab === 'posts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#FFF', fontSize: '1.2rem' }}>Blog Posts ({posts.length})</h3>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={bulkPublish} style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Publish All Drafts</button>
              <button onClick={newPost} style={{ background: gold, color: '#0A0F2A', padding: '0.4rem 0.8rem', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Plus size={14} /> New Post</button>
            </div>
          </div>
          {loading ? <p style={{ color: '#9CA3AF' }}>Loading...</p> : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: '#101632', borderRadius: '12px' }}>
              <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>No posts yet.</p>
              <button onClick={newPost} style={{ background: gold, color: '#0A0F2A', padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>Create First Post</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {posts.map(post => (
                <div key={post.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem', background: '#101632', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '4px', background: post.status === 'published' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: post.status === 'published' ? '#10B981' : '#F59E0B' }}>{post.status}</span>
                      <span style={{ fontSize: '0.7rem', color: gold }}>{post.category}</span>
                      <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>{post.source || 'manual'}</span>
                    </div>
                    <p style={{ color: '#E5E7EB', fontWeight: 600, fontSize: '0.9rem' }}>{post.title}</p>
                    <p style={{ color: '#4B5563', fontSize: '0.75rem' }}>{post.views || 0} views | /{post.slug?.slice(0, 40)}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    {post.status === 'draft' && <button onClick={() => publishPost(post)} style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', border: 'none', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer' }}><Globe size={14} /></button>}
                    <button onClick={() => setEditing(post)} style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', border: 'none', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer' }}><Edit size={14} /></button>
                    {post.status === 'published' && <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(201,169,97,0.15)', color: gold, border: 'none', borderRadius: '6px', padding: '0.4rem', display: 'flex', alignItems: 'center' }}><Eye size={14} /></a>}
                    <button onClick={() => deletePost(post.id)} style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: 'none', borderRadius: '6px', padding: '0.4rem', cursor: 'pointer' }}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ SEO AUDIT TAB ============ */}
      {activeTab === 'seo' && (
        <div>
          {seoLoading ? <p style={{ color: '#9CA3AF' }}>Analyzing SEO...</p> : !seoData ? <p style={{ color: '#9CA3AF' }}>Loading...</p> : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {[
                  { label: 'Published', value: seoData.summary.total_published, color: '#10B981' },
                  { label: 'Drafts', value: seoData.summary.total_drafts, color: '#F59E0B' },
                  { label: 'Avg SEO Score', value: `${seoData.summary.avg_seo_score}/100`, color: scoreColor(seoData.summary.avg_seo_score) },
                  { label: 'Issues Found', value: Object.values(seoData.issues).reduce((a, b) => a + b, 0), color: '#EF4444' },
                ].map(c => (
                  <div key={c.label} style={{ background: '#101632', borderRadius: '10px', padding: '1rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p style={{ color: '#6B7280', fontSize: '0.75rem', marginBottom: '0.3rem' }}>{c.label}</p>
                    <p style={{ color: c.color, fontSize: '1.5rem', fontWeight: 700 }}>{c.value}</p>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={fixAllSEO} disabled={fixingAll} style={{ background: '#EF4444', color: '#FFF', padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer', fontWeight: 700 }}>{fixingAll ? 'Fixing...' : 'Auto-Fix All SEO Issues'}</button>
                <button onClick={pingGoogle} style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Ping Google & Bing</button>
                <button onClick={buildLinks} style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7', padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Build Internal Links</button>
                <button onClick={fetchSEO} style={{ background: 'rgba(201,169,97,0.15)', color: gold, padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}><RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} />Refresh</button>
                <a href={seoData.summary.sitemap_url} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '0.45rem 0.9rem', borderRadius: '6px', fontSize: '0.8rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><ExternalLink size={12} />Sitemap</a>
              </div>

              {/* Issues Summary */}
              {Object.values(seoData.issues).some(v => v > 0) && (
                <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '1rem' }}>
                  <p style={{ color: '#FCA5A5', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Issues to Fix</p>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {seoData.issues.no_meta_desc > 0 && <span style={{ color: '#EF4444', fontSize: '0.8rem' }}>{seoData.issues.no_meta_desc} missing meta description</span>}
                    {seoData.issues.no_image > 0 && <span style={{ color: '#EF4444', fontSize: '0.8rem' }}>{seoData.issues.no_image} missing images</span>}
                    {seoData.issues.no_faq > 0 && <span style={{ color: '#F59E0B', fontSize: '0.8rem' }}>{seoData.issues.no_faq} missing FAQ schema</span>}
                    {seoData.issues.low_words > 0 && <span style={{ color: '#F59E0B', fontSize: '0.8rem' }}>{seoData.issues.low_words} low word count</span>}
                  </div>
                </div>
              )}

              {/* Per-Post SEO Audit */}
              <div>
                <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>POST-BY-POST SEO AUDIT</p>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {seoData.post_audits.map(a => (
                    <div key={a.id} style={{ background: '#101632', borderRadius: '10px', padding: '0.85rem 1rem', border: `1px solid ${scoreColor(a.seo_score)}22` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <p style={{ color: '#E5E7EB', fontWeight: 600, fontSize: '0.88rem', flex: 1 }}>{a.title}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {a.seo_score < 70 && <button onClick={() => fixSinglePost(a.id)} style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', border: 'none', cursor: 'pointer' }}>Fix</button>}
                          <span style={{ color: scoreColor(a.seo_score), fontWeight: 700, fontSize: '1rem', minWidth: '50px', textAlign: 'right' }}>{a.seo_score}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.72rem', color: '#6B7280' }}>
                        <span>{a.word_count} words</span><span>|</span>
                        <span>{a.h2_count} H2s</span><span>|</span>
                        <span>{a.faq_count} FAQs</span><span>|</span>
                        <span>{a.link_count} links</span><span>|</span>
                        <span>Title: {a.meta_title_len}ch</span><span>|</span>
                        <span>Desc: {a.meta_desc_len}ch</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                        {a.checks.map((c, i) => (
                          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.7rem', color: c.status === 'pass' ? '#10B981' : c.status === 'warn' ? '#F59E0B' : '#EF4444' }}>
                            <StatusIcon s={c.status} /> {c.item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ TRENDING KEYWORDS TAB ============ */}
      {activeTab === 'trending' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#FFF', fontSize: '1.2rem' }}>Trending Keywords</h3>
            <button onClick={discoverTrends} disabled={genLoading} style={{ background: gold, color: '#0A0F2A', padding: '0.45rem 1rem', borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}>
              {genLoading ? 'Discovering...' : 'Discover New Trends'}
            </button>
          </div>
          {seoData?.trending_keywords?.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.4rem' }}>
              {seoData.trending_keywords.map((kw, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', background: '#101632', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div>
                    <p style={{ color: '#E5E7EB', fontWeight: 600, fontSize: '0.9rem' }}>{kw.keyword}</p>
                    <p style={{ color: '#6B7280', fontSize: '0.72rem' }}>{kw.region} | {kw.type} | found {kw.discovered}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <span style={{ color: gold, fontWeight: 700, fontSize: '0.9rem' }}>{kw.volume}</span>
                    <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', borderRadius: '4px', background: kw.used ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: kw.used ? '#10B981' : '#F59E0B' }}>{kw.used ? 'Used' : 'Available'}</span>
                    {!kw.used && <button onClick={() => generatePosts(kw.keyword)} disabled={genLoading} style={{ background: 'rgba(201,169,97,0.15)', color: gold, padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', border: `1px solid ${gold}`, cursor: 'pointer' }}>Generate</button>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#6B7280', textAlign: 'center', padding: '2rem' }}>No trending keywords yet. Click "Discover New Trends" to fetch from Google.</p>
          )}
        </div>
      )}

      {/* ============ GENERATE TAB ============ */}
      {activeTab === 'generate' && (
        <div>
          <h3 style={{ color: '#FFF', fontSize: '1.2rem', marginBottom: '1rem' }}>Generate Blog Posts</h3>
          <div style={{ background: '#101632', borderRadius: '12px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Custom Keyword (leave empty for auto-pick)</label>
                <input style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#E5E7EB', padding: '0.6rem 0.85rem', width: '100%', fontSize: '0.9rem' }} value={genKeyword} onChange={e => setGenKeyword(e.target.value)} placeholder="e.g., lucky number for marriage" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '0.3rem' }}>Category</label>
                  <select style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#E5E7EB', padding: '0.6rem 0.85rem', width: '100%' }} value={genCategory} onChange={e => setGenCategory(e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <label style={{ color: '#9CA3AF', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={genAutoPublish} onChange={e => setGenAutoPublish(e.target.checked)} /> Auto-publish immediately
                  </label>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => generatePosts(genKeyword)} disabled={genLoading} style={{ background: gold, color: '#0A0F2A', padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: 700, border: 'none', cursor: 'pointer', flex: 1 }}>
                {genLoading ? 'Generating...' : genKeyword ? 'Generate 1 Custom Post' : 'Generate 3 Auto Posts'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ PIPELINE LOGS TAB ============ */}
      {activeTab === 'logs' && (
        <div>
          <h3 style={{ color: '#FFF', fontSize: '1.2rem', marginBottom: '1rem' }}>Pipeline Logs</h3>
          {seoData?.pipeline_logs?.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {seoData.pipeline_logs.map((log, i) => (
                <div key={i} style={{ background: '#101632', borderRadius: '8px', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div>
                    <p style={{ color: '#E5E7EB', fontSize: '0.88rem' }}>{log.run_at}</p>
                    <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>{log.pipeline} pipeline | {log.auto_publish ? 'auto-published' : 'drafts'}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span style={{ color: '#10B981', fontWeight: 700, fontSize: '0.9rem' }}>{log.success} ok</span>
                    {log.failed > 0 && <span style={{ color: '#EF4444', fontWeight: 700, fontSize: '0.9rem' }}>{log.failed} fail</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#6B7280', textAlign: 'center', padding: '2rem' }}>No pipeline runs yet.</p>
          )}
        </div>
      )}

      {/* ============ LIVE RANKING TAB ============ */}
      {activeTab === 'ranking' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#FFF', fontSize: '1.2rem' }}>Live Google Ranking</h3>
            <button onClick={fetchRanking} style={{ background: gold, color: '#0A0F2A', padding: '0.4rem 0.8rem', borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}>Refresh</button>
          </div>

          {!rankingData ? <p style={{ color: '#9CA3AF' }}>Loading ranking data from Google Search Console...</p>
          : rankingData.error ? (
            <div style={{ background: '#101632', borderRadius: '10px', padding: '1.5rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ color: '#F59E0B', marginBottom: '0.5rem', fontWeight: 600 }}>Google Search Console Data Not Available Yet</p>
              <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Google takes 2-3 days after verification to start showing ranking data. Check back in a few days.</p>
              <p style={{ color: '#6B7280', fontSize: '0.78rem', marginTop: '0.5rem' }}>Error: {rankingData.error}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {/* Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                <div style={{ background: '#101632', borderRadius: '10px', padding: '1rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Total Clicks</p>
                  <p style={{ color: '#10B981', fontSize: '1.5rem', fontWeight: 700 }}>{rankingData.total_clicks || 0}</p>
                </div>
                <div style={{ background: '#101632', borderRadius: '10px', padding: '1rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Total Impressions</p>
                  <p style={{ color: '#3B82F6', fontSize: '1.5rem', fontWeight: 700 }}>{rankingData.total_impressions || 0}</p>
                </div>
                <div style={{ background: '#101632', borderRadius: '10px', padding: '1rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Period</p>
                  <p style={{ color: '#9CA3AF', fontSize: '0.9rem', fontWeight: 600 }}>{rankingData.period || 'Last 28 days'}</p>
                </div>
              </div>

              {/* Top Keywords */}
              {rankingData.keywords?.length > 0 && (
                <div>
                  <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>TOP RANKING KEYWORDS</p>
                  <div style={{ display: 'grid', gap: '0.4rem' }}>
                    {rankingData.keywords.slice(0, 15).map((kw, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', background: '#101632', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ color: '#E5E7EB', fontSize: '0.88rem' }}>{kw.keyword}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.78rem' }}>
                          <span style={{ color: '#10B981' }}>{kw.clicks} clicks</span>
                          <span style={{ color: '#3B82F6' }}>{kw.impressions} imp</span>
                          <span style={{ color: '#F59E0B' }}>{kw.ctr}% CTR</span>
                          <span style={{ color: kw.position <= 10 ? '#10B981' : kw.position <= 20 ? '#F59E0B' : '#EF4444', fontWeight: 700 }}>#{Math.round(kw.position)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Page Performance */}
              {rankingData.pages?.length > 0 && (
                <div>
                  <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>PAGE PERFORMANCE</p>
                  <div style={{ display: 'grid', gap: '0.4rem' }}>
                    {rankingData.pages.map((pg, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', background: '#101632', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <p style={{ color: '#E5E7EB', fontSize: '0.82rem', flex: 1 }}>{pg.url.replace('https://astrowins.in', '')}</p>
                        <div style={{ display: 'flex', gap: '0.8rem', fontSize: '0.78rem' }}>
                          <span style={{ color: '#10B981' }}>{pg.clicks}c</span>
                          <span style={{ color: '#3B82F6' }}>{pg.impressions}i</span>
                          <span style={{ color: '#F59E0B' }}>{pg.ctr}%</span>
                          <span style={{ color: pg.position <= 10 ? '#10B981' : pg.position <= 20 ? '#F59E0B' : '#EF4444', fontWeight: 700 }}>Pos {Math.round(pg.position)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {rankingData.keywords?.length === 0 && rankingData.pages?.length === 0 && (
                <div style={{ background: '#101632', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
                  <p style={{ color: '#F59E0B', fontWeight: 600 }}>No ranking data yet</p>
                  <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Google typically takes 2-5 days after indexing to show ranking data. Your blogs were just submitted — check back soon.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ GOOGLE INDEXING TAB ============ */}
      {activeTab === 'indexing' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#FFF', fontSize: '1.2rem' }}>Google Indexing</h3>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={fetchIndexStatus} style={{ background: 'rgba(201,169,97,0.15)', color: gold, padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', border: 'none', cursor: 'pointer' }}>Refresh</button>
              <button onClick={indexAllPosts} disabled={indexLoading} style={{ background: gold, color: '#0A0F2A', padding: '0.4rem 1rem', borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}>
                {indexLoading ? 'Submitting...' : 'Submit All to Google'}
              </button>
            </div>
          </div>

          {/* Index Status Summary */}
          {indexStatus && (
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {[
                  { label: 'Total Published', value: indexStatus.total_published, color: '#94A3B8' },
                  { label: 'Indexed', value: indexStatus.indexed, color: '#10B981' },
                  { label: 'Failed', value: indexStatus.failed, color: '#EF4444' },
                  { label: 'Not Submitted', value: indexStatus.not_submitted, color: '#F59E0B' },
                ].map(c => (
                  <div key={c.label} style={{ background: '#101632', borderRadius: '10px', padding: '1rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p style={{ color: '#6B7280', fontSize: '0.75rem', marginBottom: '0.3rem' }}>{c.label}</p>
                    <p style={{ color: c.color, fontSize: '1.5rem', fontWeight: 700 }}>{c.value}</p>
                  </div>
                ))}
              </div>

              {/* Service Account Info */}
              <div style={{ background: '#101632', borderRadius: '10px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>Service Account</p>
                    <p style={{ color: '#E5E7EB', fontSize: '0.85rem', fontFamily: 'monospace' }}>{indexStatus.service_account}</p>
                  </div>
                  <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '4px', background: indexStatus.key_configured ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: indexStatus.key_configured ? '#10B981' : '#EF4444' }}>
                    {indexStatus.key_configured ? 'Key Configured' : 'Key Missing'}
                  </span>
                </div>
              </div>

              {/* Per-Post Index Status */}
              <div>
                <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>POST INDEX STATUS</p>
                <div style={{ display: 'grid', gap: '0.4rem' }}>
                  {posts.filter(p => p.status === 'published').map(post => {
                    const indexed = post.google_index_status === 'success';
                    const failed = post.google_index_status === 'error';
                    return (
                      <div key={post.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 1rem', background: '#101632', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ color: '#E5E7EB', fontSize: '0.88rem' }}>{post.title}</p>
                          <p style={{ color: '#4B5563', fontSize: '0.72rem' }}>astrowins.in/blog/{post.slug?.slice(0, 40)}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', background: indexed ? 'rgba(16,185,129,0.15)' : failed ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)', color: indexed ? '#10B981' : failed ? '#EF4444' : '#F59E0B' }}>
                            {indexed ? 'Indexed' : failed ? 'Failed' : 'Pending'}
                          </span>
                          <button onClick={() => indexSinglePost(post.slug)} style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', border: 'none', cursor: 'pointer' }}>
                            {indexed ? 'Re-submit' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!indexStatus && !indexLoading && <p style={{ color: '#6B7280', textAlign: 'center', padding: '2rem' }}>Click "Refresh" to load indexing status.</p>}
          {indexLoading && !indexStatus && <p style={{ color: '#9CA3AF' }}>Loading...</p>}
        </div>
      )}
    </div>
  );
}
